import environment from './environment';
import logger from '../utils/logger';

// API configuration using environment variables
export const apiConfig = {
  BASE_URL: environment.API_BASE_URL,
  UPLOAD_URL: environment.API_UPLOAD_URL,
  SWAGGER_URL: environment.SWAGGER_URL,
  baseURL: environment.API_BASE_URL, // For backward compatibility
};

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/v0.1/auth/login',
    LOGOUT: '/v0.1/auth/logout',
    REFRESH: '/v0.1/auth/refresh',
    ME: '/v0.1/auth/me',
    USERS: '/v0.1/auth/users',
    REGISTER: '/v0.1/auth/register',
    // 유저 관리 (admin 전용) — 권한 변경 / 삭제.
    USER_ROLE: (id: number) => `/v0.1/auth/users/${id}/role`,
    USER: (id: number) => `/v0.1/auth/users/${id}`,
  },

  // Audit log + 인앱 알림
  AUDIT: {
    LOGS: '/v0.1/audit-logs',
    NOTIFICATIONS: '/v0.1/notifications',
    NOTIFICATIONS_UNREAD_COUNT: '/v0.1/notifications/unread-count',
    NOTIFICATION_READ: (id: number | string) => `/v0.1/notifications/${id}/read`,
    NOTIFICATIONS_READ_ALL: '/v0.1/notifications/read-all',
  },

  // Projects
  PROJECTS: '/api/projects',

  // Site Management
  SITE_MANAGEMENT: {
    LIST: '/v0.1/site-management/projects',
    CREATE: '/v0.1/site-management/projects',
    GET: (id: number) => `/v0.1/site-management/projects/${id}`,
    UPDATE: (id: number) => `/v0.1/site-management/projects/${id}`,
    DELETE: (id: number) => `/v0.1/site-management/projects/${id}`,
    OPERATION_PROCESS: {
      GET_BY_SITE: (id: number) => `/v0.1/site-management/projects/${id}/operation-process`,
      UPDATE_BY_SITE: (id: number) => `/v0.1/site-management/projects/${id}/operation-process`,
      GET_TEMPLATE: '/v0.1/site-management/operation-process/template',
      UPDATE_TEMPLATE: '/v0.1/site-management/operation-process/template',
    },
    NETWORK_SPREADSHEET: {
      GET: (id: number) => `/v0.1/site-management/projects/${id}/network-spreadsheet`,
      UPDATE: (id: number) => `/v0.1/site-management/projects/${id}/network-spreadsheet`,
    },
    MATERIAL_SPREADSHEET: {
      GET: (id: number) => `/v0.1/site-management/projects/${id}/material-spreadsheet`,
      UPDATE: (id: number) => `/v0.1/site-management/projects/${id}/material-spreadsheet`,
    },
    ACCOUNT_SPREADSHEET: {
      GET: (id: number) => `/v0.1/site-management/projects/${id}/account-spreadsheet`,
      UPDATE: (id: number) => `/v0.1/site-management/projects/${id}/account-spreadsheet`,
    },
    NVR_TEMPLATE_SPREADSHEET: {
      GET: (id: number) => `/v0.1/site-management/projects/${id}/nvr-template-spreadsheet`,
      UPDATE: (id: number) => `/v0.1/site-management/projects/${id}/nvr-template-spreadsheet`,
    },
    /** 포커스AI 녹화기 원격 등록(통합서버 경유) — 미리보기·등록. */
    NVR_REGISTER: (id: number) => `/v0.1/site-management/projects/${id}/nvr-register`,
    DASHBOARD_LAYOUT: {
      GET: (id: number) => `/v0.1/site-management/projects/${id}/dashboard-layout`,
      UPDATE: (id: number) => `/v0.1/site-management/projects/${id}/dashboard-layout`,
    },
    NETWORK_DEVICES: {
      LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/network-devices`,
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/network-devices`,
      GET: (deviceId: number) => `/v0.1/site-management/network-devices/${deviceId}`,
      UPDATE: (deviceId: number) => `/v0.1/site-management/network-devices/${deviceId}`,
      DELETE: (deviceId: number) => `/v0.1/site-management/network-devices/${deviceId}`,
    },
    MATERIALS: {
      LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/materials`,
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/materials`,
      SUMMARY: (projectId: number) => `/v0.1/site-management/projects/${projectId}/materials/summary`,
      GET: (materialId: number) => `/v0.1/site-management/materials/${materialId}`,
      UPDATE: (materialId: number) => `/v0.1/site-management/materials/${materialId}`,
      DELETE: (materialId: number) => `/v0.1/site-management/materials/${materialId}`,
    },
    CONSUMABLE_NOTES: {
      LIST: '/v0.1/site-management/consumable-notes',
      COMPANY: '/v0.1/site-management/consumable-notes/company',
      PROJECT: (projectId: number) => `/v0.1/site-management/projects/${projectId}/consumable-note`,
    },
    CONTACTS: {
      LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/contacts`,
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/contacts`,
      GET: (contactId: number) => `/v0.1/site-management/contacts/${contactId}`,
      UPDATE: (contactId: number) => `/v0.1/site-management/contacts/${contactId}`,
      DELETE: (contactId: number) => `/v0.1/site-management/contacts/${contactId}`,
    },
    // 공통 QA 체크리스트(현장 목록 [QA 리스트]) + 현장별 QA 진행.
    QA: {
      TEMPLATE_LIST: '/v0.1/site-management/qa-template/items',
      TEMPLATE_CREATE: '/v0.1/site-management/qa-template/items',
      TEMPLATE_UPDATE: (itemId: number) => `/v0.1/site-management/qa-template/items/${itemId}`,
      TEMPLATE_DELETE: (itemId: number) => `/v0.1/site-management/qa-template/items/${itemId}`,
      SITE_LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/qa-items`,
      SITE_IMPORT: (projectId: number) => `/v0.1/site-management/projects/${projectId}/qa-items/import`,
      SITE_CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/qa-items`,
      SITE_UPDATE: (itemId: number) => `/v0.1/site-management/qa-items/${itemId}`,
      SITE_DELETE: (itemId: number) => `/v0.1/site-management/qa-items/${itemId}`,
      // QA 메모 — 주차 테스트 아래 댓글처럼 남기는 현장별 공유 메모.
      NOTES: {
        LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/qa-notes`,
        CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/qa-notes`,
        DELETE: (noteId: number) => `/v0.1/site-management/qa-notes/${noteId}`,
      },
    },
    LINKS: {
      LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/links`,
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/links`,
      GET: (linkId: number) => `/v0.1/site-management/links/${linkId}`,
      UPDATE: (linkId: number) => `/v0.1/site-management/links/${linkId}`,
      DELETE: (linkId: number) => `/v0.1/site-management/links/${linkId}`,
    },
    VISIT_LOGS: {
      LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/visit-logs`,
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/visit-logs`,
      GET: (logId: number) => `/v0.1/site-management/visit-logs/${logId}`,
      UPDATE: (logId: number) => `/v0.1/site-management/visit-logs/${logId}`,
      DELETE: (logId: number) => `/v0.1/site-management/visit-logs/${logId}`,
    },
    INCIDENTS: {
      LIST: (projectId: number) => `/v0.1/site-management/projects/${projectId}/incidents`,
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/incidents`,
      GET: (incidentId: number) => `/v0.1/site-management/incidents/${incidentId}`,
      UPDATE: (incidentId: number) => `/v0.1/site-management/incidents/${incidentId}`,
      DELETE: (incidentId: number) => `/v0.1/site-management/incidents/${incidentId}`,
      COMMENTS: {
        LIST: (incidentId: number) => `/v0.1/site-management/incidents/${incidentId}/comments`,
        CREATE: (incidentId: number) => `/v0.1/site-management/incidents/${incidentId}/comments`,
        DELETE: (commentId: number) => `/v0.1/site-management/incident-comments/${commentId}`,
      },
    },
    PHOTOS: {
      LIST: (projectId: number, category?: string) =>
        `/v0.1/site-management/projects/${projectId}/photos${category ? `?category=${encodeURIComponent(category)}` : ''}`,
      UPLOAD: (projectId: number) => `/v0.1/site-management/projects/${projectId}/photos`,
      GET: (photoId: number) => `/v0.1/site-management/photos/${photoId}`,
      FILE: (photoId: number) => `/v0.1/site-management/photos/${photoId}/file`,
      UPDATE: (photoId: number) => `/v0.1/site-management/photos/${photoId}`,
      DELETE: (photoId: number) => `/v0.1/site-management/photos/${photoId}`,
    },
    DOCUMENTS: {
      LIST: (projectId: number, category?: string) =>
        `/v0.1/site-management/projects/${projectId}/documents${category ? `?category=${encodeURIComponent(category)}` : ''}`,
      UPLOAD: (projectId: number) => `/v0.1/site-management/projects/${projectId}/documents`,
      FILE: (documentId: number) => `/v0.1/site-management/documents/${documentId}/file`,
      // 공지(메모) 첨부 목록. 업로드는 위 UPLOAD 에 schedule_id 를 얹는다.
      BY_SCHEDULE: (scheduleId: number) => `/v0.1/site-management/schedules/${scheduleId}/documents`,
      // 선택 문서 묶음 ZIP — ids 는 쉼표 구분. 서버가 현장 소속을 다시 확인한다.
      ZIP: (projectId: number, ids: number[]) =>
        `/v0.1/site-management/projects/${projectId}/documents/zip?ids=${ids.join(',')}`,
      DELETE: (documentId: number) => `/v0.1/site-management/documents/${documentId}`,
    },
    SCHEDULES: {
      LIST: (projectId: number, fromIso?: string, toIso?: string) => {
        const qp = new URLSearchParams();
        if (fromIso) qp.append('from', fromIso);
        if (toIso) qp.append('to', toIso);
        const q = qp.toString();
        return `/v0.1/site-management/projects/${projectId}/schedules${q ? `?${q}` : ''}`;
      },
      // 전체 현장 통합 일정 — 캘린더용. mine=true 면 작성자(created_by_id) 기준 내 일정만.
      LIST_ALL: (fromIso?: string, toIso?: string, mine?: boolean) => {
        const qp = new URLSearchParams();
        if (fromIso) qp.append('from', fromIso);
        if (toIso) qp.append('to', toIso);
        if (mine) qp.append('mine', 'true');
        const q = qp.toString();
        return `/v0.1/site-management/schedules${q ? `?${q}` : ''}`;
      },
      CREATE: (projectId: number) => `/v0.1/site-management/projects/${projectId}/schedules`,
      // 현장 없는 공통 메모. 경로에 projectId 자리가 없다는 것이 곧 '현장 미지정'이다.
      CREATE_COMMON: '/v0.1/site-management/schedules',
      GET: (id: number) => `/v0.1/site-management/schedules/${id}`,
      UPDATE: (id: number) => `/v0.1/site-management/schedules/${id}`,
      DELETE: (id: number) => `/v0.1/site-management/schedules/${id}`,
    },
  },

  // Site Control
  SITE_CONTROL: {
    GET_ALL_STATUS: '/v0.1/site-control/status',
    GET_STATUS: (site: string) => `/v0.1/site-control/${site}/status`,
    START: (site: string) => `/v0.1/site-control/${site}/start`,
    STOP: (site: string) => `/v0.1/site-control/${site}/stop`,
  },

  // Runtime info — manage-backend 호스트의 현재 구동 중인 사이트 식별.
  // docker socket 으로 smartparking-router.app 컨테이너의 NODE_ENV 를 inspect.
  RUNTIME: {
    CURRENT_SITE: '/v0.1/runtime/current-site',
    // 사내 배포 서버에서 서비스별로 떠 있는 이미지 버전 (배포 화면 상단).
    SERVICE_VERSIONS: '/v0.1/runtime/service-versions',
    // 사내 배포 서버에 있는 서비스 이미지의 vX.Y.Z 버전 — 현장 통합서버 승격 목록.
    IMAGE_VERSIONS: (appname: string) => `/v0.1/runtime/image-versions/${encodeURIComponent(appname)}`,
  },

  // Simulator — manage-backend 의 in-memory simulator 세션.
  // CCTVS 는 맵(map_projects.id) 기준 — MapData.cctvs.lightCctvs + site_network_devices IP join.
  SIMULATOR: {
    START: '/v0.1/simulator/start',
    STOP: '/v0.1/simulator/stop',
    STATUS: '/v0.1/simulator/status',
    CCTVS: (mapId: number) => `/v0.1/simulator/maps/${mapId}/cctvs`,
    EMERGENCY_BELLS: (mapId: number) => `/v0.1/simulator/maps/${mapId}/emergency-bells`,
  },

  // 주차 점유 시뮬레이터 — manage-backend 가 현장 router 의 시뮬레이션 API 로 프록시.
  PARKING_SIMULATOR: {
    LOTS: '/v0.1/parking-simulator/lots',
    STATUS: '/v0.1/parking-simulator/status',
    START: '/v0.1/parking-simulator/start',
    STOP: '/v0.1/parking-simulator/stop',
  },

  // 조명 시뮬레이터 — 조명 on/off 요청이 현장 lights 서비스까지 도달하는지 체크.
  // dry-run 캡처 모드(MODE/EVENTS)로 fan-out 결과를 관찰한다.
  LIGHTS_SIMULATOR: {
    DIMMING: '/v0.1/lights-simulator/dimming',
    MODE: '/v0.1/lights-simulator/mode',
    EVENTS: '/v0.1/lights-simulator/events',
    MOTION: '/v0.1/lights-simulator/motion',
    EMERGENCY: '/v0.1/lights-simulator/emergency',
    BELL: '/v0.1/lights-simulator/bell',
    CONTROL: '/v0.1/lights-simulator/control',
  },

  // Map Editor — 사이트의 맵 프로젝트 목록 (시뮬레이터 맵 드롭다운 등에서 사용).
  MAP_EDITOR: {
    LIST_PROJECTS: (siteProjectId: number) =>
      `/v0.1/map-editor/projects?site_project_id=${siteProjectId}`,
  },

  // File Upload
  UPLOAD_LEARNING: (projectId: string) => `/v0.1/parking/${projectId}/train-images`,
  UPLOAD_TEST: (projectId: string) => `/v0.1/parking/${projectId}/test-images`,
  UPLOAD_ROI: (projectId: string) => `/v0.1/parking/${projectId}/roi-files`,

  // Folders
  GET_FOLDERS: (projectId: string, fileType: string) => `/api/folders/${projectId}/${fileType}`,
  GET_LEARNING_FOLDERS: (projectId: string) => `/v0.1/parking/${projectId}/images/train-folders`,
  GET_TEST_FOLDERS: (projectId: string) => `/v0.1/parking/${projectId}/images/test-folders`,
  GET_ROI_FOLDERS: (projectId: string) => `/v0.1/parking/${projectId}/images/roi-folders`,
  DELETE_FILE_OR_FOLDER: (projectId: string, folderPath: string) => `/v0.1/parking/${projectId}/${folderPath}`,

  // Parking Test
  PARKING_TEST: '/api/parking-test',

  // Learning
  LEARNING: (projectId: string) => `/v0.1/parking/${projectId}/learning`,
  LEARNING_LIVE: (projectId: string) => `/v0.1/parking/${projectId}/learning/live`,

  // Batch Images
  BATCH_IMAGES: (projectId: string) => `/v0.1/parking/${projectId}/images/batch`,

  // Learning Results
  LEARNING_RESULTS: (projectId: string, folderPath: string) => `/v0.1/parking/${projectId}/learning-results/${folderPath}`,
  LEARNING_HISTORY: (projectId: string) => `/v0.1/parking/${projectId}/history`,

  // CCTV Images
  CCTV_IMAGE: (projectId: string, folderPath: string, cctvId: string, imageType: string) =>
    `/v0.1/parking/${projectId}/${folderPath}/${cctvId}/images/${imageType}`,
  REALTIME_CCTV_IMAGE: (projectId: string, cctvId: string, imageType: string) =>
    `/v0.1/parking/${projectId}/${cctvId}/images/${imageType}`,

  // ROI
  GET_ROI_IMAGES: (projectId: string, folderPath: string) => `/v0.1/roi/${projectId}/${folderPath}/images`,
  GET_ROI_IMAGE: (projectId: string, folderPath: string, fileName: string) => `/v0.1/roi/${projectId}/${folderPath}?file=${fileName}`,
  CREATE_DRAFT_ROI: (projectId: string, roiFileName: string) => `/v0.1/roi/${projectId}/draft?file=${roiFileName}`,
  GET_DRAFT_ROI: (projectId: string, roiFileName: string) => `/v0.1/roi/${projectId}/draft?file=${roiFileName}`,
  SAVE_DRAFT_ROI: (projectId: string, roiFileName: string) => `/v0.1/roi/${projectId}/draft/save?file=${roiFileName}`,
  CREATE_ROI: (projectId: string) => `/v0.1/roi/${projectId}/create`,
  READ_ROI: (projectId: string) => `/v0.1/roi/${projectId}/read`,
  UPDATE_ROI: (projectId: string) => `/v0.1/roi/${projectId}/update`,
  DELETE_ROI: (projectId: string) => `/v0.1/roi/${projectId}/delete`,

  // File Storage API v0.1
  FILE_STORAGE: {
    AUTO_UPLOAD_MAP: (projectId: string) => `/v0.1/filestorage/${projectId}/map/auto-upload`,
    UPLOAD: (projectId: string, category: string, mapId?: string) => {
      const base = `/v0.1/filestorage/${projectId}/${category}/upload`;
      return mapId ? `${base}?map_id=${mapId}` : base;
    },
    LIST: (projectId: string, category: string, params?: {
      page?: number;
      pageSize?: number;
      cctvId?: string;
      mapId?: string;
    }) => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.pageSize) queryParams.append('page_size', params.pageSize.toString());
      if (params?.cctvId) queryParams.append('cctv_id', params.cctvId);
      if (params?.mapId) queryParams.append('map_id', params.mapId);
      const query = queryParams.toString();
      return `/v0.1/filestorage/${projectId}/${category}/list${query ? '?' + query : ''}`;
    },
    LIST_FOLDERS: (projectId: string, category: string) => `/v0.1/filestorage/${projectId}/${category}/folders`,
    DOWNLOAD: (projectId: string, category: string, filename: string, mapId?: string) => {
      const base = `/v0.1/filestorage/${projectId}/${category}/download/${filename}`;
      return mapId ? `${base}?map_id=${mapId}` : base;
    },
    DOWNLOAD_LATEST: (projectId: string, category: string, originalName: string) =>
      `/v0.1/filestorage/${projectId}/${category}/latest?original_name=${encodeURIComponent(originalName)}`,
    DOWNLOAD_MULTIPLE: (projectId: string, category: string) => `/v0.1/filestorage/${projectId}/${category}/download-multiple`,
    DELETE: (projectId: string, category: string, filename: string) => {
      const encodedPath = filename
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');
      return `/v0.1/filestorage/${projectId}/${category}/delete/${encodedPath}`;
    },
    BATCH_DELETE: (projectId: string, category: string) => `/v0.1/filestorage/${projectId}/${category}/batch-delete`,
    DELETE_FOLDER: (projectId: string, category: string, folderPath: string) => {
      const encodedPath = folderPath
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');
      return `/v0.1/filestorage/${projectId}/${category}/folder/${encodedPath}`;
    },
    DEDUP_FILTER: (projectId: string) => `/v0.1/filestorage/${projectId}/learning/dedup-filter`,
  },

  // Swagger
  SWAGGER: '/swagger/index.html'
};

// Axios config for backward compatibility
export const axiosConfig = {
  baseURL: environment.API_BASE_URL,
  timeout: 300000, // 5분 — 무거운 백엔드 응답 대기 허용

  headers: {
    'Content-Type': 'application/json',
  }
};

// Development logging
if (environment.NODE_ENV === 'development') {
  logger.info('🔧 API Configuration:', {
    BASE_URL: apiConfig.BASE_URL,
    UPLOAD_URL: apiConfig.UPLOAD_URL,
    SWAGGER_URL: apiConfig.SWAGGER_URL,
    NODE_ENV: environment.NODE_ENV
  });
}

export default apiConfig;
